import type { ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import { Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CONTACT_ENDPOINT_NON_JSON_ERROR, sendContactLead } from '../../../lib/contactApi';

type ContactFormData = {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  inquiryType: string;
  budget: string;
  message: string;
};

type ContactFieldErrors = Partial<Record<keyof ContactFormData, string>>;

const INSTAGRAM_URL = 'https://www.instagram.com/kamilfrajtko';
const LINKEDIN_URL = 'https://www.linkedin.com/in/kamilfrajtko/';
const WHATSAPP_URL = 'https://wa.me/421905929167';

const initialFormData: ContactFormData = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  inquiryType: '',
  budget: '',
  message: '',
};

function FieldLabel({ children }: { children: string }) {
  return <label className="mb-2 block text-sm font-medium text-white/82">{children}</label>;
}

function fieldClassName(isInvalid = false) {
  return `w-full rounded-2xl border bg-black/20 px-4 py-3.5 text-sm text-white outline-none transition-all placeholder:text-white/32 focus:border-brand-green/45 focus:bg-black/28 ${isInvalid ? 'border-[#ff8f8f]/35' : 'border-white/10'}`;
}

function mapFieldErrors(
  errors: Record<string, string> | undefined,
  t: TFunction<'translation'>
): ContactFieldErrors {
  if (!errors) {
    return {};
  }

  return {
    fullName: errors.fullName ? t('contact.form.errors.fullName') : undefined,
    email: errors.email ? t('contact.form.errors.email') : undefined,
    message: errors.message ? t('contact.form.errors.message') : undefined,
    inquiryType: errors.inquiryType ? t('contact.form.errors.inquiryType') : undefined,
  };
}

export default function ContactInfoBento() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const inquiryTypes = t('contact.form.inquiryTypes', { returnObjects: true }) as string[];
  const budgetOptions = t('contact.form.budgetOptions', { returnObjects: true }) as string[];
  const contactLinks = [
    {
      label: t('contact.info.instagram'),
      href: INSTAGRAM_URL,
      icon: 'ri-instagram-line',
    },
    {
      label: t('contact.info.linkedIn'),
      href: LINKEDIN_URL,
      icon: 'ri-linkedin-box-line',
    },
    {
      label: t('contact.info.whatsapp'),
      href: WHATSAPP_URL,
      icon: 'ri-whatsapp-line',
    },
  ];

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({ ...current, [name]: undefined }));

    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setSubmitMessage('');
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitStatus('submitting');
    setSubmitMessage('');
    setFieldErrors({});

    try {
      const result = await sendContactLead({
        source: 'contact-page',
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        inquiryType: formData.inquiryType,
        budget: formData.budget,
        message: formData.message,
      });

      if (result.ok) {
        setSubmitStatus('success');
        setSubmitMessage(t('contact.form.successMessage'));
        setFormData(initialFormData);
        return;
      }

      if (!result.ok) {
        setSubmitStatus('error');
        setSubmitMessage(t('contact.form.genericError'));
        setFieldErrors(mapFieldErrors(('errors' in result ? result.errors : undefined) ?? undefined, t));
        return;
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(
        error instanceof Error && error.message === CONTACT_ENDPOINT_NON_JSON_ERROR
          ? t('contact.form.endpointError')
          : t('contact.form.genericError')
      );
    }
  };

  return (
    <section className="relative z-20 -mt-12 pb-24 md:-mt-24">

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)]">
          <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.9)_0%,rgba(26,26,26,0.76)_100%)] p-7 shadow-[0_22px_60px_rgba(0,0,0,0.32)] backdrop-blur-2xl md:p-10">
            <Chip
              variant="flat"
              className="mb-5 border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green"
              startContent={
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-mail-send-line text-base" />
                </span>
              }
          >
              {t('contact.form.badge')}
            </Chip>

            <h2 className="text-3xl font-caprasimo text-white md:text-4xl">{t('contact.form.title')}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-white/62 md:text-base">
              {t('contact.form.description')}
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6" id="contact-form">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <FieldLabel>{t('contact.form.fullName')}</FieldLabel>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className={fieldClassName(Boolean(fieldErrors.fullName))}
                    placeholder={t('contact.form.placeholders.fullName')}
                  />
                  {fieldErrors.fullName ? (
                    <p className="mt-2 text-xs text-[#ffb4b4]">{fieldErrors.fullName}</p>
                  ) : null}
                </div>
                <div>
                  <FieldLabel>{t('contact.form.email')}</FieldLabel>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={fieldClassName(Boolean(fieldErrors.email))}
                    placeholder={t('contact.form.placeholders.email')}
                  />
                  {fieldErrors.email ? (
                    <p className="mt-2 text-xs text-[#ffb4b4]">{fieldErrors.email}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <FieldLabel>{t('contact.form.phone')}</FieldLabel>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={fieldClassName()}
                    placeholder={t('contact.form.placeholders.phone')}
                  />
                </div>
                <div>
                  <FieldLabel>{t('contact.form.company')}</FieldLabel>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={fieldClassName()}
                    placeholder={t('contact.form.placeholders.company')}
                  />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <FieldLabel>{t('contact.form.inquiryType')}</FieldLabel>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className={fieldClassName(Boolean(fieldErrors.inquiryType))}
                  >
                    <option value="" className="bg-[#121212] text-white/70">
                      {t('contact.form.placeholders.inquiryType')}
                    </option>
                    {inquiryTypes.map((option) => (
                      <option key={option} value={option} className="bg-[#121212] text-white">
                        {option}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.inquiryType ? (
                    <p className="mt-2 text-xs text-[#ffb4b4]">{fieldErrors.inquiryType}</p>
                  ) : null}
                </div>
                <div>
                  <FieldLabel>{t('contact.form.budget')}</FieldLabel>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className={fieldClassName()}
                  >
                    <option value="" className="bg-[#121212] text-white/70">
                      {t('contact.form.placeholders.budget')}
                    </option>
                    {budgetOptions.map((option) => (
                      <option key={option} value={option} className="bg-[#121212] text-white">
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <FieldLabel>{t('contact.form.message')}</FieldLabel>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={7}
                  className={`${fieldClassName(Boolean(fieldErrors.message))} resize-none`}
                  placeholder={t('contact.form.placeholders.message')}
                />
                {fieldErrors.message ? (
                  <p className="mt-2 text-xs text-[#ffb4b4]">{fieldErrors.message}</p>
                ) : null}
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="inline-flex w-full items-center justify-between rounded-[20px] border border-white/10 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-4 text-base font-black text-[#0D0D0D] shadow-[0_16px_30px_rgba(163,230,53,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(163,230,53,0.24)] md:w-auto md:min-w-[240px]"
                >
                  <span>
                    {submitStatus === 'submitting'
                      ? t('contact.form.submitting')
                      : t('contact.form.submit')}
                  </span>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/10">
                    <i
                      className={`text-base ${submitStatus === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'}`}
                    />
                  </span>
                </button>
                <p className="text-sm text-white/52">{t('contact.form.replyNote')}</p>
                {submitStatus === 'success' ? (
                  <div className="rounded-2xl border border-brand-green/18 bg-brand-green/[0.08] px-4 py-3 text-sm text-white/82">
                    {submitMessage}
                  </div>
                ) : null}
                {submitStatus === 'error' ? (
                  <div className="rounded-2xl border border-[#ff8f8f]/18 bg-[#ff8f8f]/[0.08] px-4 py-3 text-sm text-white/82">
                    {submitMessage}
                  </div>
                ) : null}
              </div>
            </form>
          </div>

          <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.88)_0%,rgba(26,26,26,0.72)_100%)] p-6 shadow-[0_18px_48px_rgba(0,0,0,0.28)] backdrop-blur-2xl md:p-7">
            <div className="pointer-events-none absolute bottom-[-72px] right-[-56px] h-48 w-48 rounded-full bg-brand-ocean/[0.14] blur-3xl" />
            <div className="pointer-events-none absolute bottom-[20px] right-[18px] h-24 w-24 rounded-full bg-white/[0.05] blur-2xl" />

            <div className="relative z-10">
            <Chip
              variant="flat"
              className="mb-5 border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green"
              startContent={
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-contacts-book-2-line text-base" />
                </span>
              }
            >
              {t('contact.info.badge')}
            </Chip>

            <div className="flex flex-wrap gap-3">
              {contactLinks.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.label}
                  title={item.label}
                  className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.03)_100%)] text-brand-green shadow-[0_12px_28px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.04)] transition-all hover:-translate-y-0.5 hover:bg-[linear-gradient(180deg,rgba(163,230,53,0.18)_0%,rgba(163,230,53,0.08)_100%)] hover:text-white"
                >
                  <i className={`${item.icon} text-[1.35rem]`} />
                </a>
              ))}
            </div>

            <div className="mt-6 border-t border-white/8 pt-6">
              <h3 className="text-lg font-semibold text-white">{t('contact.info.billingTitle')}</h3>
              <div className="mt-3 space-y-1.5 text-sm leading-6 text-white/68">
                <p className="text-white/84">{t('contact.info.businessName')}</p>
                <p>{t('contact.info.ico')}</p>
                <p>{t('contact.info.dic')}</p>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
